import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const StudentDetailModal = ({ student, classrooms, onClose, onSave, onRegenerateCredentials }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(student?.fullName || '');
  const [editStatus, setEditStatus] = useState(student?.status || 'active');
  const [editClassroomId, setEditClassroomId] = useState(student?.classroomId || '');
  const [editGradeLevel, setEditGradeLevel] = useState(student?.gradeLevel || '');
  const [saving, setSaving] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [currentCredentials, setCurrentCredentials] = useState({
    username: student?.username,
    password: student?.plainPassword
  });
  const [showPassword, setShowPassword] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const classroom = classrooms?.find(c => c?.id === student?.classroomId);
  const editClassroom = classrooms?.find(c => c?.id === editClassroomId);

  const getStatusLabel = (status) => {
    switch (status) {
      case 'active': return 'Active';
      case 'inactive': return 'Inactive';
      case 'suspended': return 'Suspended';
      default: return status || '—';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const handleEdit = () => {
    setEditName(student?.fullName || '');
    setEditStatus(student?.status || 'active');
    setEditClassroomId(student?.classroomId || '');
    setEditGradeLevel(student?.gradeLevel || '');
    setIsEditing(true);
  };

  const handleCancel = () => {
    setEditName(student?.fullName || '');
    setEditStatus(student?.status || 'active');
    setEditClassroomId(student?.classroomId || '');
    setEditGradeLevel(student?.gradeLevel || '');
    setIsEditing(false);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await onSave(student?.id, { fullName: editName, status: editStatus, classroomId: editClassroomId || null, gradeLevel: editGradeLevel || null });
      setSaveSuccess(true);
      setIsEditing(false);
      setTimeout(() => setSaveSuccess(false), 2000);
    } catch (err) {
      console.error('Save error:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleRegenerate = async () => {
    if (!window.confirm('Generate new User ID and password for this student? They will need the new credentials to log in.')) return;
    try {
      setRegenerating(true);
      const updated = await onRegenerateCredentials(student?.id);
      setCurrentCredentials({
        username: updated?.username,
        password: updated?.plainPassword
      });
      setShowPassword(true);
    } catch (err) {
      console.error('Regenerate error:', err);
    } finally {
      setRegenerating(false);
    }
  };

  const handlePrint = () => {
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Student Credentials - ${editName}</title>
        <style>
          body { font-family: Arial, sans-serif; display: flex; justify-content: center; align-items: center; min-height: 100vh; margin: 0; background: #f5f5f5; }
          .card { background: white; border: 2px solid #e5e7eb; border-radius: 12px; padding: 32px; max-width: 360px; width: 100%; box-shadow: 0 4px 12px rgba(0,0,0,0.1); }
          .header { text-align: center; margin-bottom: 24px; }
          .avatar { width: 64px; height: 64px; background: #dcfce7; border-radius: 50%; display: flex; align-items: center; justify-content: center; margin: 0 auto 12px; font-size: 24px; font-weight: bold; color: #16a34a; }
          .name { font-size: 20px; font-weight: bold; color: #111827; margin-bottom: 4px; }
          .classroom { font-size: 14px; color: #6b7280; }
          .divider { border: none; border-top: 1px solid #e5e7eb; margin: 20px 0; }
          .label { font-size: 11px; color: #9ca3af; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 4px; }
          .value { font-family: monospace; font-size: 18px; font-weight: bold; color: #111827; background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 6px; padding: 8px 12px; margin-bottom: 16px; }
          .footer { text-align: center; font-size: 11px; color: #9ca3af; margin-top: 20px; }
          @media print { body { background: white; } }
        </style>
      </head>
      <body>
        <div class="card">
          <div class="header">
            <div class="avatar">${editName?.charAt(0)?.toUpperCase()}</div>
            <div class="name">${editName}</div>
            <div class="classroom">${classroom?.classroomName || classroom?.name || 'No Classroom'}</div>
          </div>
          <hr class="divider" />
          <div class="label">User ID</div>
          <div class="value">${currentCredentials?.username || 'N/A'}</div>
          <div class="label">Password</div>
          <div class="value">${currentCredentials?.password || '••••'}</div>
          <hr class="divider" />
          <div class="footer">Keep this card safe. Do not share your credentials.</div>
        </div>
      </body>
      </html>
    `;
    const printWindow = window.open('', '_blank');
    printWindow?.document?.write(printContent);
    printWindow?.document?.close();
    printWindow?.focus();
    setTimeout(() => printWindow?.print(), 300);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1001] p-4 pt-20">
      <div className="bg-card rounded-xl shadow-2xl w-full max-w-lg max-h-[calc(100vh-5rem)] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
              <span className="text-lg font-bold text-primary">
                {(editName || student?.fullName)?.charAt(0)?.toUpperCase()}
              </span>
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">Student Details</h2>
              <p className="text-sm font-medium text-foreground">{editName || student?.fullName}</p>
              <p className="text-xs text-muted-foreground">{student?.username}</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {!isEditing && (
              <button
                onClick={handleEdit}
                className="flex items-center space-x-1.5 px-3 py-1.5 bg-primary/10 hover:bg-primary/20 text-primary rounded-lg text-sm font-medium transition-colors"
              >
                <Icon name="Pencil" size={14} />
                <span>Edit</span>
              </button>
            )}
            <button
              onClick={onClose}
              className="p-2 hover:bg-muted rounded-lg transition-colors"
            >
              <Icon name="X" size={20} className="text-muted-foreground" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Profile Info */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-muted/40 rounded-lg p-3">
              <p className="text-xs text-muted-foreground mb-1">Classroom</p>
              <p className="text-sm font-medium text-foreground">{classroom?.classroomName || classroom?.name || 'Not assigned'}</p>
            </div>
            <div className="bg-muted/40 rounded-lg p-3">
              <p className="text-xs text-muted-foreground mb-1">Grade</p>
              <p className="text-sm font-medium text-foreground">{classroom?.gradeLevel || student?.gradeLevel || '—'}</p>
            </div>
            <div className="bg-muted/40 rounded-lg p-3">
              <p className="text-xs text-muted-foreground mb-1">Last Active</p>
              <p className="text-sm font-medium text-foreground">{formatDate(student?.lastActive)}</p>
            </div>
            <div className="bg-muted/40 rounded-lg p-3">
              <p className="text-xs text-muted-foreground mb-1">Progress</p>
              <div className="flex items-center space-x-2">
                <div className="flex items-center space-x-1">
                  <Icon name="CheckCircle" size={14} className="text-success" />
                  <span className="text-sm font-medium text-foreground">{student?.journeysCompleted || 0}</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Icon name="Clock" size={14} className="text-warning" />
                  <span className="text-sm font-medium text-foreground">{student?.journeysInProgress || 0}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Edit / View Section */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-foreground">Student Info</h3>

            {isEditing ? (
              /* Edit Mode */
              <>
                <div>
                  <label className="block text-xs text-muted-foreground mb-1.5">Full Name</label>
                  <input
                    type="text"
                    value={editName}
                    onChange={(e) => setEditName(e?.target?.value)}
                    className="w-full px-3 py-2 border border-input rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground"
                  />
                </div>
                <div>
                  <label className="block text-xs text-muted-foreground mb-1.5">Classroom</label>
                  <select
                    value={editClassroomId}
                    onChange={(e) => setEditClassroomId(e?.target?.value)}
                    className="w-full px-3 py-2 border border-input rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground"
                  >
                    <option value="">No Classroom</option>
                    {classrooms?.map(c => (
                      <option key={c?.id} value={c?.id}>{c?.classroomName || c?.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-muted-foreground mb-1.5">Grade Level</label>
                  <select
                    value={editGradeLevel}
                    onChange={(e) => setEditGradeLevel(e?.target?.value)}
                    className="w-full px-3 py-2 border border-input rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground"
                  >
                    <option value="">Select Grade</option>
                    <option value="Primary 1">Primary 1</option>
                    <option value="Primary 2">Primary 2</option>
                    <option value="Primary 3">Primary 3</option>
                    <option value="Primary 4">Primary 4</option>
                    <option value="Primary 5">Primary 5</option>
                    <option value="Primary 6">Primary 6</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-muted-foreground mb-1.5">Status</label>
                  <select
                    value={editStatus}
                    onChange={(e) => setEditStatus(e?.target?.value)}
                    className="w-full px-3 py-2 border border-input rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="suspended">Suspended</option>
                  </select>
                </div>
                <div className="flex space-x-3">
                  <button
                    onClick={handleCancel}
                    className="flex-1 px-4 py-2.5 border border-border rounded-lg text-sm font-medium text-foreground hover:bg-muted transition-colors"
                  >
                    Cancel
                  </button>
                  <Button
                    onClick={handleSave}
                    disabled={saving}
                    iconName={saveSuccess ? 'Check' : 'Save'}
                    className="flex-1"
                  >
                    {saving ? 'Saving...' : saveSuccess ? 'Saved!' : 'Save Changes'}
                  </Button>
                </div>
              </>
            ) : (
              /* View Mode */
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs text-muted-foreground mb-0.5">Full Name</p>
                    <p className="text-sm font-medium text-foreground">{student?.fullName || '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-0.5">Status</p>
                    <p className="text-sm font-medium text-foreground capitalize">{getStatusLabel(student?.status)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-0.5">Classroom</p>
                    <p className="text-sm font-medium text-foreground">{classroom?.classroomName || classroom?.name || 'Not assigned'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-0.5">Grade Level</p>
                    <p className="text-sm font-medium text-foreground">{student?.gradeLevel || classroom?.gradeLevel || '—'}</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Credentials Section */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-foreground">Credentials</h3>
            <div className="bg-muted/40 rounded-lg p-4 space-y-3">
              <div>
                <p className="text-xs text-muted-foreground mb-1">User ID</p>
                <code className="text-sm font-mono font-medium text-foreground bg-background px-3 py-1.5 rounded border border-border block">
                  {currentCredentials?.username || 'N/A'}
                </code>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Password</p>
                <div className="flex items-center space-x-2">
                  <code className="flex-1 text-sm font-mono font-medium text-foreground bg-background px-3 py-1.5 rounded border border-border">
                    {showPassword ? (currentCredentials?.password || 'N/A') : '••••'}
                  </code>
                  <button
                    onClick={() => setShowPassword(!showPassword)}
                    className="p-2 hover:bg-muted rounded-lg transition-colors"
                  >
                    <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={16} className="text-muted-foreground" />
                  </button>
                </div>
              </div>
            </div>

            {/* Regenerate Button */}
            <button
              onClick={handleRegenerate}
              disabled={regenerating}
              className="w-full flex items-center justify-center space-x-2 px-4 py-2.5 border border-border rounded-lg text-sm font-medium text-foreground hover:bg-muted transition-colors disabled:opacity-50"
            >
              <Icon name={regenerating ? 'Loader2' : 'RefreshCw'} size={16} className={regenerating ? 'animate-spin' : ''} />
              <span>{regenerating ? 'Generating...' : 'Regenerate Credentials'}</span>
            </button>
          </div>

          {/* Print Button */}
          <button
            onClick={handlePrint}
            className="w-full flex items-center justify-center space-x-2 px-4 py-2.5 bg-primary/10 hover:bg-primary/20 rounded-lg text-sm font-medium text-primary transition-colors"
          >
            <Icon name="Printer" size={16} />
            <span>Print Credentials Card</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default StudentDetailModal;
